import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useEffectOnce, usePathParam } from '@chilecompra/react-kit'
import { Dialog } from '@chilecompra/react-kit/components'

import { SELECTION_CRITERIA_DICTIONARY, SELECT_OFFER_DIALOG_STATES_DICTIONARY } from '../../config/settings/constants'
import { topPage } from '../../modules/utils/topPage'
import { inIframe } from '../../modules/utils/inIframe'

import {
  onCloseSelectOfferDialog,
  onCreatePurcharseOrder,
  onDisableLowerOffer,
  onGetSelectionCriteriaThunk,
  onSavePurchaseOrderThunk,
  onValidationInputGroup
} from './SelectOfferDialog.actions'
import { onGetQuoteSummaryThunk } from '../QuoteBasicInformation/QuoteBasicInformation.actions'

import SelectOfferDialogFooter from './SelectOfferDialog.footer'
import SelectOfferDialogHeader from './SelectOfferDialog.header'
import SelectOfferDialogList from './SelectOfferDialog.list'
import SelectOfferSuccess from './SelectOfferDialog.success'
import SelectOfferError from './SelectOfferDialog.error'

import { DialogWrapper } from './SelectOfferDialog.styles'

/**
 * The Select Offer Dialog's container.
 */
const SelectOfferDialog = () => {
  const dispatch = useDispatch()
  const quoteId = usePathParam('quoteId')

  const {
    isOfferLowerPrice,
    loading,
    purchaseOrderInfo,
    purchaseOrders,
    selectedOffers,
    showSelectOfferDialog,
    validationInputGroup
  } = useSelector(state => state.selectOfferDialog)
  const {
    bidding,
    basicInfo: { currency }
  } = useSelector(state => state.quoteBasicInformation)

  const [criteriaError, setCriteriaError] = useState(false)
  const [offerSelected, setOfferSelected] = useState([])
  const [statePurchaseOrder, setIssuePurchaseOrder] = useState(SELECT_OFFER_DIALOG_STATES_DICTIONARY.INITIAL)
  const [selectOfferDialogWidth, setSelectOfferDialogWidth] = useState('600px')

  const getOfferSelected = selectedOffers.map(offerId => Object.values(bidding).find(offer => offer.bidId === offerId))
  const isIframe = useMemo(() => inIframe(), [])

  const loadSelectedOffers = () => {
    if (getOfferSelected.length > 1) {
      const input = getOfferSelected.map(offer => ({ ref: String(offer.bidId), validation: false }))
      dispatch(onDisableLowerOffer())
      dispatch(onValidationInputGroup({ input }))
      return getOfferSelected.map(offer => ({
        idOffer: String(offer.bidId),
        selectionCriteria: {
          idCriteria: SELECTION_CRITERIA_DICTIONARY.OTHER,
          text: ''
        }
      }))
    }
    return getOfferSelected.map(offer => ({
      idOffer: String(offer.bidId)
    }))
  }
  const validatePurchaseOrder = () => {
    if (criteriaError) return true
    if (!purchaseOrders?.purchaseOrder?.every(offer => offer.selectionCriteria)) return true

    if (getOfferSelected.length > 1) {
      const inputGroup = validationInputGroup.filter(input => input.validation === true)
      return inputGroup.length !== getOfferSelected.length
    }

    return false
  }
  const memoizedDisabledButtonPurchaseOrder = useMemo(validatePurchaseOrder, [
    criteriaError,
    purchaseOrders.purchaseOrder,
    getOfferSelected.length,
    validationInputGroup
  ])

  const handleClickCloseDialog = () => {
    dispatch(onCloseSelectOfferDialog())
    setIssuePurchaseOrder(SELECT_OFFER_DIALOG_STATES_DICTIONARY.INITIAL)
  }
  const handleSubmit = () => {
    dispatch(
      onSavePurchaseOrderThunk({
        ...purchaseOrders,
        callback: () => {
          dispatch(onGetQuoteSummaryThunk({ quoteId }))
          setIssuePurchaseOrder(SELECT_OFFER_DIALOG_STATES_DICTIONARY.SUCCESS)
          topPage(isIframe, showSelectOfferDialog)
        },
        error: () => {
          setIssuePurchaseOrder(SELECT_OFFER_DIALOG_STATES_DICTIONARY.ERROR)
          topPage(isIframe, showSelectOfferDialog)
        }
      })
    )
  }

  const handleCriteriaError = useCallback(err => setCriteriaError(err), [])

  useEffectOnce(() => {
    dispatch(onGetSelectionCriteriaThunk())
  })

  useEffect(() => {
    setOfferSelected(getOfferSelected)
  }, [selectedOffers])

  useEffect(() => {
    topPage(isIframe, showSelectOfferDialog)

    dispatch(onCreatePurcharseOrder({ idRequest: quoteId, purchaseOrder: loadSelectedOffers() }))
  }, [showSelectOfferDialog])

  useEffect(() => {
    const uniqueCriteria = !isOfferLowerPrice && selectedOffers.length === 1
    const mutlipleCriteria = selectedOffers.length > 1

    if (statePurchaseOrder === SELECT_OFFER_DIALOG_STATES_DICTIONARY.INITIAL && (uniqueCriteria || mutlipleCriteria)) {
      setSelectOfferDialogWidth('800px')
    } else {
      setSelectOfferDialogWidth('600px')
    }
  }, [statePurchaseOrder, selectedOffers, isOfferLowerPrice])

  return (
    <Dialog
      disableBackdropClick
      maxWidth={selectOfferDialogWidth}
      onClose={handleClickCloseDialog}
      open={showSelectOfferDialog}
      verticalAlign="top"
    >
      {statePurchaseOrder === SELECT_OFFER_DIALOG_STATES_DICTIONARY.INITIAL && (
        <DialogWrapper>
          <SelectOfferDialogHeader
            currency={currency}
            isOfferLowerPrice={isOfferLowerPrice}
            offerSelected={offerSelected}
            offerCounter={selectedOffers.length}
          />
          <SelectOfferDialogList currency={currency} offerSelected={offerSelected} onChanges={handleCriteriaError} />
          <SelectOfferDialogFooter
            disabledPurchaseOrder={memoizedDisabledButtonPurchaseOrder}
            isOfferLowerPrice={isOfferLowerPrice}
            loading={loading}
            offerCounter={selectedOffers.length}
            onClose={handleClickCloseDialog}
            onSubmit={handleSubmit}
          />
        </DialogWrapper>
      )}
      {statePurchaseOrder === SELECT_OFFER_DIALOG_STATES_DICTIONARY.SUCCESS && (
        <SelectOfferSuccess onClose={handleClickCloseDialog} purchaseInfo={purchaseOrderInfo} />
      )}
      {statePurchaseOrder === SELECT_OFFER_DIALOG_STATES_DICTIONARY.ERROR && (
        <SelectOfferError onClose={handleClickCloseDialog} />
      )}
    </Dialog>
  )
}

export default SelectOfferDialog
